import { Link as RouterLink, useParams, useLocation } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Page } from "../../../common/components/Page";
import { usePublicFacility } from "../hooks/usePublicFacility";
import { AuthContext } from "@bw/shared/hooks";
import { useContext } from "react";
import { FacilityHero } from "../components/FacilityHero";
import { FacilityPhotos } from "../components/FacilityPhotos";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import { FacilityLocation } from "../components/FacilityLocation";
import { FacilityFacts } from "../components/FacilityFacts";
import { FacilityOfferings } from "../components/FacilityOfferings";
import { FacilityCompetitions } from "../components/FacilityCompetitions";
import { FacilityPostGallery } from "../components/FacilityPostGallery";
import { FacilityCampaignOffers } from "../components/FacilityCampaignOffers";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";
import { readSearchReturn } from "../../../common/searchNavigation";

/**
 * The player-facing FACILITY HOMEPAGE (`/facility/:facilityId`) — the hub reached
 * from the discovery list cards + map markers, sitting BETWEEN discovery and the
 * court-booking grid. It composes existing booking reads (facility / courts /
 * operating-hours projections) for the primary content and the facility's community feed for a
 * secondary post gallery and public campaign terms.
 *
 * Structure (top to bottom):
 *   - HERO       — name, address and service-gated booking action.
 *   - OFFERS     — public campaign terms; eligibility is checked during checkout.
 *   - PROGRAMS   — classes / drop-in (gated), by path.
 *   - SUPPORT    — court count, weekly hours, services and location.
 *   - GALLERY    — secondary club feed; beside support on desktop.
 *
 * Thin presentation only: every decision is a render gate on SERVER data
 * (`enabled_services`, the courts/operating-hours lists). Distance is
 * OMITTED — the facility GET returns none and computing it client-side is a
 * forbidden business-logic leak.
 *
 * A facility load failure (404 / 5xx) is an HONEST page-level error with a real
 * "Back to facilities" <Link> to `/book` — NOT a redirect, NOT a blank page, and
 * NOT an auto-navigation into the booking grid. On that path the gallery is never
 * mounted, so its community/feed reads never fire (no facility → no community).
 *
 * The AppShell supplies the chrome but no page title; this routed page owns its
 * single <h1> via the hero's facility name (WCAG 1.3.1 / 2.4.6).
 */
export function FacilityHomePage() {
  const location = useLocation();
  const searchReturn = readSearchReturn(location.search);
  const backToResults =
    searchReturn ?? readFacilityDiscoveryReturn(location.search) ?? "/play";
  const backLabel = searchReturn ? "Back to search" : "Back to facilities";
  const { facilityId } = useParams<{ facilityId: string }>();
  const facilityQ = usePublicFacility(facilityId);
  // Composed for the FACTS strip; both stay disabled until the id resolves and
  // are independent reads (a slow courts/hours read never blocks the hero).
  const user = useContext(AuthContext)?.user ?? null;

  // ---- facility load states (the PRIMARY read gates the page) --------------
  if (facilityQ.isLoading) {
    return (
      <Page>
        <Stack
          direction="row"
          spacing={1.5}
          role="status"
          sx={{ py: 4, alignItems: "center", justifyContent: "center" }}
        >
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading facility…</Typography>
        </Stack>
      </Page>
    );
  }

  if (facilityQ.isError || !facilityQ.data) {
    // Honest page-level error + a real way back to the list. The gallery is NOT
    // rendered here, so its community/feed reads never fire for a dead facility.
    return (
      <Page>
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              component={RouterLink}
              to={backToResults}
              color="inherit"
              size="small"
            >
              {backLabel}
            </Button>
          }
        >
          We couldn&apos;t load this facility. It may not exist.
        </Alert>
      </Page>
    );
  }

  const facility = facilityQ.data;
  const enabledServices = facility.enabled_services ?? [];
  const canBookCourt =
    enabledServices.includes("court_reservation") &&
    (facility.ready_services ?? []).includes("court_reservation");

  return (
    <Page>
      <Stack spacing={2.5} sx={{ width: "100%", maxWidth: 720, minWidth: 0 }}>
        <FacilityHero
          facility={facility}
          back={
            <Tooltip title={backLabel}>
              <IconButton component={RouterLink} to={backToResults} aria-label={backLabel} edge="start">
                <ArrowBackRounded />
              </IconButton>
            </Tooltip>
          }
          primaryAction={
            canBookCourt ? (
              <Button
                component={RouterLink}
                to={withFacilityDiscoveryReturn(`/book/${encodeURIComponent(facility.facility_id)}`, backToResults)}
                variant="contained"
                fullWidth
                sx={{ borderRadius: 999 }}
              >
                Book a court
              </Button>
            ) : null
          }
        >
          {!canBookCourt && enabledServices.includes("court_reservation") ? (
            <Typography variant="body2" color="text.secondary">
              Online court booking is not available yet.
            </Typography>
          ) : null}
        </FacilityHero>
        <FacilityPhotos
          key={facility.facility_id}
          photos={facility.photos ?? []}
        />
        {(facility.promotion_summaries?.length ?? 0) > 0 ? (
          <FacilityCampaignOffers
            facilityId={facility.facility_id}
            promotions={facility.promotion_summaries ?? []}
            facilitySearchReturn={backToResults}
          />
        ) : null}
        <FacilityOfferings
          facilityId={facility.facility_id}
          enabledServices={enabledServices}
          readyServices={facility.ready_services ?? []}
          facilitySearchReturn={backToResults}
        />
        <FacilityCompetitions facilityId={facility.facility_id} />
        <FacilityFacts
          courts={[]}
          publicCourtCount={facility.court_count}
          projections={facility.operating_hours ?? []}
          enabledServices={enabledServices}
        />
        <FacilityLocation facility={facility} />
        {/* Secondary, self-contained: resolves the facility's community then its
          feed, and renders nothing (no chrome) when there is no club. */}
        {user ? <FacilityPostGallery facilityId={facility.facility_id} /> : null}
      </Stack>
    </Page>
  );
}
